/**
 * BScroll 智能滚动 mixin
 *
 * 提供基于点击位置的智能滚动功能，适用于横向滚动的选项列表
 *
 * 使用方式：
 * 1. 在组件中引入：import bScrollSmartScroll from '@mixins/bScrollSmartScroll'
 * 2. 混入组件：mixins: [bScrollSmartScroll]
 * 3. 确保组件有以下必要元素：
 *    - scroll: BScroll 实例
 *    - 容器元素的 ref
 *    - 选项元素的 ref 数组
 * 4. 调用：this.smartScrollToItem(index, containerRef, itemsLength, getItemRef)
 *
 * 功能特性：
 * - 边缘点击智能露出相邻选项
 * - 兼容不同宽度的选项
 * - 首尾选项特殊处理
 * - 平滑动画效果
 */

export default {
  methods: {
    /**
     * 智能滚动到指定选项
     * @param {number} index - 目标选项索引
     * @param {string} containerRef - 容器元素的 ref 名称
     * @param {number} itemsLength - 选项总数
     * @param {Function} getItemRef - 获取选项元素的函数，参数为 index，返回 DOM 元素
     * @param {Object} options - 配置选项
     * @param {number} options.animationDuration - 动画持续时间，默认 300ms
     * @param {number} options.edgeTolerance - 边缘容错像素，默认 50px
     * @param {number} options.maxExposeWidth - 最大露出宽度，默认 100px
     * @param {number} options.endMargin - 最后一个元素的右边距，默认 20px
     * @param {boolean} options.refreshOnZeroMax - maxScrollX 为 0 时尝试 refresh，默认 true
     */
    smartScrollToItem(
      index,
      containerRef,
      itemsLength,
      getItemRef,
      options = {}
    ) {
      const {
        animationDuration = 300,
        edgeTolerance = 50,
        maxExposeWidth = 100,
        endMargin = 20,
        refreshOnZeroMax = true
      } = options;

      // 检查必要的参数
      if (
        !this.scroll ||
        typeof index !== 'number' ||
        index < 0 ||
        index >= itemsLength
      ) {
        console.warn('smartScrollToItem: 无效的参数');
        return;
      }

      // 获取目标元素
      const targetElement = getItemRef(index);
      if (!targetElement) {
        console.warn(`smartScrollToItem: 无法获取索引 ${index} 的元素`);
        return;
      }

      // 获取容器元素（兼容 $refs 数组/单值）
      const containerRefVal = this.$refs[containerRef];
      const container = Array.isArray(containerRefVal)
        ? containerRefVal[0]
        : containerRefVal;
      if (!container) {
        console.warn(`smartScrollToItem: 无法获取容器 ${containerRef}`);
        return;
      }

      // 读取并缓存布局数据，避免重复强制重排
      const containerWidth = container.offsetWidth;
      if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
        // 容器不可见或宽度为 0，跳过滚动
        return;
      }

      // 计算水平滚动的最大可达距离，用于边界夹紧，避免触发回弹
      let maxScrollXAbs = Math.abs(this.scroll.maxScrollX || 0);
      // 若没有可滚动距离但内容明显超出容器，尝试 refresh 以兼容 iOS/Android 边缘场景
      if (refreshOnZeroMax && maxScrollXAbs === 0) {
        const lastEl = getItemRef(itemsLength - 1);
        if (lastEl) {
          const contentRight =
            lastEl.offsetLeft + lastEl.offsetWidth + endMargin;
          if (contentRight > containerWidth) {
            // 进行一次 refresh 来更新 maxScrollX
            if (typeof this.scroll.refresh === 'function') {
              this.scroll.refresh();
              maxScrollXAbs = Math.abs(this.scroll.maxScrollX || 0);
            }
          }
        }
      }
      const clampScrollX = value => {
        const clamped = Math.min(Math.max(0, value), maxScrollXAbs);
        // 取整避免 Safari/iOS 的亚像素抖动
        return Math.round(clamped);
      };

      // 计算当前可见区域
      const scrollLeft = Math.abs(this.scroll.x);
      const visibleRight = scrollLeft + containerWidth;

      // 计算目标元素的位置
      const elementWidth = targetElement.offsetWidth;
      if (!Number.isFinite(elementWidth) || elementWidth <= 0) {
        return;
      }
      const elementLeft = targetElement.offsetLeft;
      const elementRight = elementLeft + elementWidth;

      // 判断元素在可见区域的位置
      const isAtRightEdge = elementRight > visibleRight - edgeTolerance;
      const isAtLeftEdge = elementLeft < scrollLeft + edgeTolerance;
      const isFirstElement = index === 0;
      const isLastElement = index === itemsLength - 1;

      if (isLastElement && elementRight > visibleRight) {
        // 如果是最后一个元素且没有完全显示，滚动让它完全显示
        const overflowDistance = elementRight - visibleRight;
        const newScrollX = clampScrollX(
          scrollLeft + overflowDistance + endMargin
        );
        this.scroll.scrollTo(-newScrollX, 0, animationDuration);
      } else if (isFirstElement && elementLeft < scrollLeft) {
        // 如果是第一个元素且没有完全显示，滚动到最开始
        this.scroll.scrollTo(0, 0, animationDuration);
      } else if (isAtRightEdge && !isLastElement) {
        // 如果点击的是可见区域最右边的元素且不是最后一个元素
        // 滚动让下一个选项部分可见（露出一部分提示用户还有更多选项）
        const nextElement = getItemRef(index + 1);
        if (nextElement) {
          const nextElementWidth = nextElement.offsetWidth;
          if (!Number.isFinite(nextElementWidth) || nextElementWidth <= 0) {
            // 下一个元素不可用，直接返回
            return;
          }
          const nextElementRight = nextElement.offsetLeft + nextElementWidth;
          // 计算需要滚动的距离，让下一个元素至少露出一半或最大露出宽度（取较小值）
          const exposeWidth = Math.min(nextElementWidth / 2, maxExposeWidth);
          const targetPosition =
            nextElementRight - containerWidth + exposeWidth;
          const newScrollX = clampScrollX(targetPosition); // 夹紧到边界，避免回弹
          this.scroll.scrollTo(-newScrollX, 0, animationDuration);
        }
      } else if (isAtLeftEdge && !isFirstElement) {
        // 如果点击的是可见区域最左边的元素且不是第一个元素
        // 滚动让前一个选项部分可见（露出一部分提示用户前面还有选项）
        const prevElement = getItemRef(index - 1);
        if (prevElement) {
          const prevElementWidth = prevElement.offsetWidth;
          if (!Number.isFinite(prevElementWidth) || prevElementWidth <= 0) {
            return;
          }
          // 计算需要滚动的距离，让前一个元素至少露出一半或最大露出宽度（取较小值）
          const exposeWidth = Math.min(prevElementWidth / 2, maxExposeWidth);
          const targetPosition = prevElement.offsetLeft - exposeWidth;
          const newScrollX = clampScrollX(targetPosition); // 夹紧到边界，避免回弹
          this.scroll.scrollTo(-newScrollX, 0, animationDuration);
        }
      } else if (elementLeft < scrollLeft || elementRight > visibleRight) {
        // 如果目标元素不完全可见，计算目标位置并进行边界夹紧，避免回弹
        const targetPosition =
          elementLeft < scrollLeft
            ? elementLeft
            : elementRight - containerWidth;
        const newScrollX = clampScrollX(targetPosition);
        this.scroll.scrollTo(-newScrollX, 0, animationDuration);
      }
      // 如果元素已经完全可见且不在边缘，则不滚动
    },

    /**
     * 简化版本的智能滚动，适用于使用 $refs 的场景
     * @param {number} index - 目标选项索引
     * @param {string} containerRef - 容器元素的 ref 名称
     * @param {string} itemRefPrefix - 选项元素 ref 的前缀（如 'item'，会访问 this.$refs[itemRefPrefix + index]）
     * @param {number} itemsLength - 选项总数
     * @param {Object} options - 配置选项
     */
    smartScrollToRefItem(
      index,
      containerRef,
      itemRefPrefix,
      itemsLength,
      options = {}
    ) {
      const getItemRef = itemIndex => {
        const refKey = itemRefPrefix + itemIndex;
        const refArray = this.$refs[refKey];
        return Array.isArray(refArray) ? refArray[0] : refArray;
      };

      this.smartScrollToItem(
        index,
        containerRef,
        itemsLength,
        getItemRef,
        options
      );
    },

    /**
     * 智能滚动到居中位置（兼容旧的居中滚动需求）
     * @param {number} index - 目标选项索引
     * @param {string} containerRef - 容器元素的 ref 名称
     * @param {Function} getItemRef - 获取选项元素的函数
     * @param {number} animationDuration - 动画持续时间，默认 300ms
     */
    scrollToCenter(index, containerRef, getItemRef, animationDuration = 300) {
      const targetElement = getItemRef(index);
      const container = this.$refs[containerRef];

      if (!this.scroll || !targetElement || !container) {
        return;
      }

      const containerWidth = container.offsetWidth;
      const elementWidth = targetElement.offsetWidth;
      const elementLeft = targetElement.offsetLeft;

      // 计算最大可滚动距离，居中滚动时进行边界夹紧，避免回弹
      const maxScrollXAbs = Math.abs(this.scroll.maxScrollX || 0);
      const clampScrollX = value => Math.min(Math.max(0, value), maxScrollXAbs);

      // 计算居中偏移量
      const offsetX = (containerWidth - elementWidth) / 2;

      // 计算目标滚动位置（使元素中心与容器中心对齐），并夹紧到边界
      const targetPosition =
        elementLeft + elementWidth / 2 - containerWidth / 2;
      const newScrollX = clampScrollX(targetPosition);
      this.scroll.scrollTo(-newScrollX, 0, animationDuration);
    }
  }
};
